import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { ReplyAll } from "lucide-react";
import { useState } from "react";
import { useRequestNewCard } from "../../hooks/useRequestNewCard";
import { useQueryClient } from "@tanstack/react-query";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import SelectController from "@/common/components/SelectController";
import TextArea from "@/common/components/TextArea";
import { SubmitHandler, useForm } from "react-hook-form";
import useDebounce from "@/common/hooks/useDebounce";
import { useCardRequestReasons } from "../../hooks/useCardRequestReasons";
import { requestNewCardSchema, RequestNewCardValidationSchema } from "../../schemas/requestNewCard.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormTransformers } from "@/common/utils/formTransformers";
import { Mapper } from "@/common/services/mapper";
import { CardRequestReasonsTransformer } from "../../services/cardRequestReasonsTransformer";

const RequestNewCard = ({
  id,
  disabled,
}: {
  id: string
  disabled?: boolean
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const [searchCardRequestReasons, setSearchCardRequestReasons] = useDebounce('');

  const { data: cardRequestReasons, isPending: cardRequestReasonsIsPending } = useCardRequestReasons({
    filters: { search: searchCardRequestReasons },
    mapper: Mapper.mapWithTransformer({ transformer: CardRequestReasonsTransformer.optionsTransformer() }),
  });

  const { mutate: requestNewCard } = useRequestNewCard({
    id,
    onSuccess: () => {
      setIsOpen(false);
      setIsError(false);
      setMessages(["Cartão solicitado"]);
      reset();
    },
    onError: (error) => {
      setIsError(true);
      FormTransformers.errorsTransformer({ setError, setMessages })(error);
    }
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<RequestNewCardValidationSchema>({
    resolver: zodResolver(requestNewCardSchema),
  });

  const onSubmit: SubmitHandler<RequestNewCardValidationSchema> = (form) => requestNewCard(form);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={(value) => {
          reset();
          setIsOpen(value);
        }}
        onConfirm={handleSubmit(onSubmit)}
        Trigger={
          <Button
            operationKlassToCheckPermission="Admin::Client::Cards::RequestNewCard"
            variant="outline-text"
            className="px-0 text-yellow-400"
            disabled={disabled}
          >
            Emitir 2° via do cartão

            <ReplyAll strokeWidth={1.5} className="w-5" />
          </Button>
        }
        triggerDisabled={disabled}
        useCancellableFooter
        title="Emitir segunda via do cartão"
        body={(
          <form className="flex flex-col gap-4">
            <SelectController
              control={control}
              name="request_reason"
              label="Motivo da 2ª via"
              markAsRequired
              onInputChange={setSearchCardRequestReasons}
              options={cardRequestReasons}
              isLoading={cardRequestReasonsIsPending}
              errorMessage={errors.request_reason?.message || undefined}
            />

            <TextArea
              label="Suas observações"
              markAsRequired
              {...register('request_observation')}
              errorMessage={errors.request_observation?.message || undefined}
            />
          </form>
        )}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={isError}
          onClose={() => {
            queryClient.invalidateQueries({ queryKey: ['clientCards'] });
            queryClient.invalidateQueries({ queryKey: ['clientCardsTotalizer'] });
          }}
        />
      )}
    </>
  );
};

export default RequestNewCard;
